import styles from '@/pages/app/Enterprises/components/styles.module.scss'

import type { BillingAddress, BillingInfo } from '@/api/generated'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'

interface EnterpriseFormData {
  email: string
  domain: string
  name: string
}

interface SalesLedDetails {
  plan: string
  billsNotetaker: boolean
  seats: number
  monthlyPlatformFee: string
}

interface AdminProvisioning {
  firstName: string
  lastName: string
}

interface CustomPricingDetails {
  unitPriceCents: number
  pricePeriod: 'year' | 'month'
  preview: string
}

interface ConfirmEnterpriseCreationDialogProps {
  isOpen: boolean
  formData: EnterpriseFormData | null
  isLoading: boolean
  onConfirm: () => void
  onCancel: () => void
  salesLedDetails?: SalesLedDetails
  customPricing?: CustomPricingDetails
  billingInfo?: BillingInfo
  adminProvisioning?: AdminProvisioning
}

const formatAddress = (address?: BillingAddress | null): string | undefined => {
  if (!address) return undefined
  const parts = [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(', '),
    address.postal_code,
    address.country,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : undefined
}

const ConfirmEnterpriseCreationDialog = ({
  isOpen,
  formData,
  isLoading,
  onConfirm,
  onCancel,
  salesLedDetails,
  customPricing,
  billingInfo,
  adminProvisioning,
}: ConfirmEnterpriseCreationDialogProps) => {
  const isSalesLed = !!salesLedDetails?.plan
  const title = isSalesLed ? 'Confirm enterprise creation with subscription' : 'Confirm enterprise creation'
  const subtitle = isSalesLed
    ? 'This will create an enterprise with a Stripe subscription and a draft invoice. The invoice will not be sent until you add a PO number from the Manage tab.'
    : 'You are about to create a new enterprise with the following details. The user with the email you provide will be used as the admin for this enterprise.'

  const addressLine = formatAddress(billingInfo?.address)

  return (
    <DialogWrapper isOpen={isOpen} title={title} subtitle={subtitle} close={onCancel}>
      <div className={styles.dialogContent}>
        {formData && (
          <div className={styles.dialogDetailsSection}>
            <div className={styles.dialogNotificationDetails}>
              <p className={styles.dialogDetailItem}>
                <strong>Admin Email:</strong>
                <span>{formData.email}</span>
              </p>
              <p className={styles.dialogDetailItem}>
                <strong>Domain:</strong>
                <span>{formData.domain}</span>
              </p>
              <p className={styles.dialogDetailItem}>
                <strong>Enterprise Name:</strong>
                <span>{formData.name}</span>
              </p>
              {isSalesLed && (
                <>
                  <p className={styles.dialogDetailItem}>
                    <strong>Plan:</strong>
                    <span>{salesLedDetails.plan}</span>
                  </p>
                  <p className={styles.dialogDetailItem}>
                    <span>The plan cannot be changed after creation.</span>
                  </p>
                  {salesLedDetails.billsNotetaker && (
                    <p className={styles.dialogDetailItem}>
                      <span>Notetaker is switched on for the customer automatically when this deal is created.</span>
                    </p>
                  )}
                  <p className={styles.dialogDetailItem}>
                    <strong>Seats:</strong>
                    <span>{salesLedDetails.seats}</span>
                  </p>
                  <p className={styles.dialogDetailItem}>
                    <strong>Platform fee:</strong>
                    <span>{salesLedDetails.monthlyPlatformFee}</span>
                  </p>
                </>
              )}
              {adminProvisioning && (
                <p className={styles.dialogDetailItem}>
                  <strong>New Admin Name:</strong>
                  <span>
                    {adminProvisioning.firstName} {adminProvisioning.lastName}
                  </span>
                </p>
              )}
              {customPricing && (
                <p className={styles.dialogDetailItem}>
                  <strong>Custom Unit Price:</strong>
                  <span>
                    {customPricing.unitPriceCents} USD cents/seat/{customPricing.pricePeriod}
                    {customPricing.preview && ` — ${customPricing.preview}`}
                  </span>
                </p>
              )}
              {billingInfo?.billing_contact && (
                <p className={styles.dialogDetailItem}>
                  <strong>Billing Contact</strong>
                  <span>{billingInfo.billing_contact}</span>
                </p>
              )}
              {addressLine && (
                <p className={styles.dialogDetailItem}>
                  <strong>Billing Address</strong>
                  <span>{addressLine}</span>
                </p>
              )}
            </div>
            {adminProvisioning && formData && (
              <div className={styles.dialogWarningMessage}>
                A new Wispr account will be created for <strong>{formData.email}</strong> ({adminProvisioning.firstName}{' '}
                {adminProvisioning.lastName}) and a password-reset email will be sent to that address. Double-check the
                email before confirming.
              </div>
            )}
            {isSalesLed && (
              <div className={styles.dialogWarningMessage}>
                A draft invoice will be created. You&apos;ll need to add the PO number and send it later from the Manage
                tab.
              </div>
            )}
          </div>
        )}
        <div className={styles.dialogActions}>
          <Button title="Cancel" onClick={onCancel} type="light" />
          <Button
            title={isSalesLed ? 'Create Enterprise with Subscription' : 'Create Enterprise'}
            onClick={onConfirm}
            type="dark"
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}

export default ConfirmEnterpriseCreationDialog
